<script setup lang="ts">
import type { ShopTheLookTypeFragment } from '#graphql-operations'

const props = withDefaults(defineProps<{
  look: ShopTheLookTypeFragment
  lazy: boolean
  lookCount?: number
  fullWidth?: boolean
}>(), {
  lookCount: 1,
  fullWidth: false,
})

const uniqueId = useId()
const dialogName = `look-${props.look.documentId}-${uniqueId}`

const { open } = useUiDialog(dialogName)

const hasContent = computed(() => {
  return !!(props.look?.Title || props.look?.Description || props.look?.Button?.Text)
})

const widescreenImage = computed(() => {
  const columns = Math.min(props.lookCount, 3)
  const maxWidth = Math.round((props.fullWidth ? 2560 : 1920) / columns)

  if (columns === 1) {
    return useXcdnImage(props.look?.Image?.url, 'f=rs:fit:' + maxWidth)
  }

  return useXcdnImage(props.look?.ImageMobile?.url, 'f=rs:fit:' + maxWidth)
})

const imageWidth = computed(() => {
  if (props.lookCount === 1) {
    return props.look?.Image?.width
  }
  return props.look?.ImageMobile?.width
})

const imageHeight = computed(() => {
  if (props.lookCount === 1) {
    return props.look?.Image?.height
  }
  return props.look?.ImageMobile?.height
})

const desktopImage = computed(() => {
  const columns = Math.min(props.lookCount, 3)
  const maxWidth = Math.round(1280 / columns)
  return useXcdnImage(props.look?.ImageMobile?.url, 'f=rs:fit:' + maxWidth)
})

const tabletImage = computed(() => {
  const columns = Math.min(props.lookCount, 2)
  const maxWidth = Math.round(1024 / columns)
  return useXcdnImage(props.look?.ImageMobile?.url, 'f=rs:fit:' + maxWidth)
})

const phabletImage = computed(() => {
  const columns = Math.min(props.lookCount, 2)
  const maxWidth = Math.round(768 / columns)
  return useXcdnImage(props.look?.ImageMobile?.url || props.look?.Image?.Media?.url, 'f=rs:fit:' + maxWidth)
})

const mobileImage = computed(() => {
  const columns = Math.min(props.lookCount, 2)
  const maxWidth = Math.round(640 / columns)
  return useXcdnImage(props.look?.ImageMobile?.url || props.look?.Image?.Media?.url, 'f=rs:fit:' + maxWidth)
})
</script>

<template>
  <article
    class="look-tile"
    :class="{ 'look-tile--overlay': hasContent }"
  >
    <div class="look-tile__container">
      <div
        v-if="hasContent"
        class="look-tile__content"
      >
        <UiHeading
          v-if="look?.Title"
          class="look-tile__heading"
        >
          {{ look.Title }}
        </UiHeading>

        <p
          v-if="look?.Description"
          class="look-tile__description"
        >
          {{ look.Description }}
        </p>

        <AppLink
          v-if="look?.Button?.Link || look?.Button?.Text"
          class="look-tile__button"
          :class="`look-tile__button--${look?.Button?.Type}`"
        >
          <span>{{ look?.Button?.Text }}</span>
        </AppLink>
      </div>
    </div>

    <button
      v-if="look?.Products?.length"
      type="button"
      class="look-tile__products"
      @click="open"
    >
      <span class="look-tile__products-label">
        Alle items ({{ look.Products.length }})
      </span>

      <img
        v-for="{ product } in look.Products.slice(0, 3)"
        :key="product"
        :src="product.image"
        :alt="product.name"
        class="look-tile__products-product"
        height="48"
        width="48"
        loading="lazy"
        fetchpriority="low"
        decoding="async"
      >

      <!-- <pre>{{ look.Products }}</pre> -->
    </button>

    <div class="look-tile__visual">
      <picture
        v-if="look?.Image?.url && look?.Image?.mime?.includes('image')"
        class="look-tile__picture"
      >
        <source
          :srcset="widescreenImage"
          media="(min-width: 1280px)"
        >
        <source
          :srcset="desktopImage"
          media="(min-width: 1024px)"
        >
        <source
          :srcset="tabletImage"
          media="(min-width: 768px)"
        >
        <source
          :srcset="phabletImage"
          media="(min-width: 640px)"
        >
        <source
          :srcset="mobileImage"
        >
        <img
          class="look-tile__image"
          :src="widescreenImage"
          :alt="look?.Image?.alternativeText || look?.Title || ''"
          :width="imageWidth"
          :height="imageHeight"
          :loading="lazy ? 'lazy' : 'eager'"
          :fetchpriority="lazy ? 'low' : 'high'"
          :decoding="lazy ? 'async' : 'sync'"
        >
      </picture>
    </div>

    <slot name="look-additional" />

    <LookModal
      v-if="look?.Products?.length"
      :name="dialogName"
      :look="look"
    />

    <!-- <pre>columns: {{ Math.min(props.lookCount, 3) }}</pre> -->
    <!-- <pre>lookCount: {{ lookCount }}</pre> -->
    <!-- <pre>fullWidth: {{ fullWidth }}</pre> -->
    <!-- <pre>Look: {{ look }}</pre> -->
  </article>
</template>

<style>
@import url("~layers/components/Look/Tile.css");
</style>
